/**
 * Identity capture modal — shown the first time a reviewer opens ?review=true,
 * and re-openable from the header chip if they want to change their name.
 */

import { useState, FormEvent } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { useReviewSession } from './useReviewSession';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  videoTitle: string | null;
  /** When true, the modal cannot be dismissed without entering a name. */
  required?: boolean;
}

export function ReviewerIdentityModal({ open, onOpenChange, videoTitle, required = false }: Props) {
  const identity = useReviewSession((s) => s.identity);
  const setIdentity = useReviewSession((s) => s.setIdentity);

  const [name, setName] = useState(identity?.name ?? '');
  const [email, setEmail] = useState(identity?.email ?? '');
  const [touched, setTouched] = useState(false);

  const trimmed = name.trim();
  const valid = trimmed.length > 0;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid) return;
    setIdentity(trimmed, email.trim() || undefined);
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (required && !o && !identity) return; // can't dismiss without identity
        onOpenChange(o);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reviewing {videoTitle ? `“${videoTitle}”` : 'this video'}</DialogTitle>
          <DialogDescription>
            Tell the creator who you are. Your comments will be sent to them with your name attached.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="reviewer-name">
              Your name
            </label>
            <Input
              id="reviewer-name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Smith"
              maxLength={120}
              aria-invalid={touched && !valid}
            />
            {touched && !valid && (
              <p className="text-xs text-destructive">Please enter a name so the creator knows who reviewed.</p>
            )}
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="reviewer-email">
              Email <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <Input
              id="reviewer-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jane@company.com"
              maxLength={254}
            />
            <p className="text-xs text-muted-foreground">
              Only used if the creator wants to reply directly. Never displayed publicly.
            </p>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            {!required && (
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={!valid}>
              Start reviewing
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
